import { useState } from 'react';
import { Lock, Mail, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { adminLogin } from './services/admin_api';
import { storage } from '@/services/storage';
import { extractErrorMessage } from '@/services/api';

interface Props {
  onSuccess: () => void;
}

// AdminLogin is the portal's sole public page. Same look as the
// taskpane login but branded + wider (this runs in a browser, not the
// Excel sidebar).
export function AdminLogin({ onSuccess }: Props) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const resp = await adminLogin(email.trim(), password);
      storage.setTokens(resp.access_token, resp.refresh_token);
      storage.setUser(resp.user);
      toast.success(`Welcome, ${resp.user.email}`);
      onSuccess();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Login failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-card">
        <div className="mb-5 flex flex-col items-center gap-2 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary font-bold">
            EA
          </div>
          <h1 className="text-xl font-semibold">Excel Autopilot — Admin</h1>
          <p className="text-xs text-text-secondary">
            Sign in to manage users and AI configuration
          </p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Email</span>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={busy}
                className="input pl-8"
                placeholder="admin@example.com"
              />
            </div>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-text-secondary">Password</span>
            <div className="relative">
              <Lock className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" />
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={busy}
                className="input pl-8"
                placeholder="••••••••"
              />
            </div>
          </label>
          <button type="submit" disabled={busy} className="btn-primary w-full">
            <LogIn className="mr-2 h-4 w-4" />
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
